import { Box } from '@mui/material';
import React, { Suspense, useCallback, useState } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from 'react-router-dom';
import LoadingSpinner from './components/common/RetroLoading';
import LoginScreen from './components/Login';
import { authUrl } from './config/authUrls';
import { useAuth } from './contexts/Auth';
import ChatAppShell from './design/ChatAppShell';
import { isLabEnabled } from './config/lab';
import { ROUTES, routeConfig } from './routes';

function App() {
  const { token, login, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false); // eslint-disable-line no-unused-vars

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const handleLoginError = useCallback((error) => {
    console.error('Login Failed:', error);
  }, []);

  const handleLogin = useCallback(
    async (decodedToken, authToken) => {
      setIsLoading(true);
      try {
        const response = await fetch(authUrl('/login/google'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: authToken }),
        });

        if (!response.ok) {
          let detail = `HTTP error! status: ${response.status}`;
          try {
            const errBody = await response.json();
            if (errBody?.error) detail = errBody.error;
          } catch {
            /* ignore */
          }
          if (response.status === 403) {
            throw new Error(
              detail === 'Unauthorized email'
                ? 'This Google account is not on the allowlist.'
                : detail
            );
          }
          throw new Error(detail);
        }

        const data = await response.json();
        // Only the server-issued session token enters the app here. The
        // raw Google ID token must not — using it would mount the shell
        // before the session exists and fail every API call.
        if (!data?.token) {
          throw new Error('Login response missing session token');
        }
        login(data.user, data.token);
      } catch (error) {
        console.error('Login error:', error);
        handleLoginError(error?.message || error);
      } finally {
        setIsLoading(false);
      }
    },
    [login, handleLoginError]
  );

  const handleDevLogin = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(authUrl('/login/dev'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`Dev login failed: ${response.status}`);
      }
      const data = await response.json();
      login(data.user, data.token);
    } catch (error) {
      console.error('Dev login error:', error);
      handleLoginError(error);
    } finally {
      setIsLoading(false);
    }
  }, [login, handleLoginError]);

  return (
    <Router>
      <AppRoutes
        token={token}
        handleLogin={handleLogin}
        handleLoginError={handleLoginError}
        handleDevLogin={handleDevLogin}
        handleLogout={handleLogout}
      />
    </Router>
  );
}

function AppRoutes({
  token,
  handleLogin,
  handleLoginError,
  handleDevLogin,
  handleLogout,
}) {
  const location = useLocation();
  const isLab = isLabEnabled() && location.pathname.startsWith('/lab/');
  const isLegal =
    location.pathname === ROUTES.PRIVACY || location.pathname === ROUTES.TERMS;

  if (!token && !isLab && !isLegal) {
    return (
      <LoginScreen
        onLogin={handleLogin}
        onError={handleLoginError}
        onDevLogin={
          process.env.REACT_APP_DEV_AUTH === '1' ? handleDevLogin : undefined
        }
      />
    );
  }

  const routes = (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {routeConfig
          .filter(
            (route) => !String(route.path).startsWith('/lab/') || isLabEnabled()
          )
          .map((route, index) => (
            <Route
              key={index}
              path={route.path}
              element={<route.component />}
            />
          ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {token ? (
        <ChatAppShell onLogout={handleLogout}>{routes}</ChatAppShell>
      ) : (
        routes
      )}
    </Box>
  );
}

export default App;
